<script setup lang="ts">
import { ref } from 'vue'
import { useFormStore } from '@/stores/form'

const emit = defineEmits<{
  (e: 'submit'): void
}>()

const formStore = useFormStore()

const month = ref(formStore.data.dobMonth || '')
const day = ref(formStore.data.dobDay || '')
const year = ref(formStore.data.dobYear || '')

const showValidation = ref(false)
const monthError = ref('')
const dayError = ref('')
const yearError = ref('')

const monthInput = ref<HTMLInputElement | null>(null)
const dayInput = ref<HTMLInputElement | null>(null)
const yearInput = ref<HTMLInputElement | null>(null)

const currentYear = new Date().getFullYear()

function validateMonth(): boolean {
  const m = parseInt(month.value, 10)
  if (!month.value || isNaN(m) || m < 1 || m > 12) {
    monthError.value = 'Invalid month'
    return false
  }
  monthError.value = ''
  return true
}

function validateDay(): boolean {
  const d = parseInt(day.value, 10)
  if (!day.value || isNaN(d) || d < 1 || d > 31) {
    dayError.value = 'Invalid day'
    return false
  }
  dayError.value = ''
  return true
}

function validateYear(): boolean {
  const y = parseInt(year.value, 10)
  if (!year.value || isNaN(y) || y < (currentYear - 120) || y > (currentYear - 18)) {
    yearError.value = 'Must be 18-120 years old'
    return false
  }
  yearError.value = ''
  return true
}

function handleMonthInput(e: Event) {
  const input = e.target as HTMLInputElement
  let value = input.value.replace(/\D/g, '').slice(0, 2)

  // Auto-pad and advance
  if (value.length === 1 && parseInt(value) > 1) {
    value = '0' + value
  }

  month.value = value

  if (value.length === 2) {
    dayInput.value?.focus()
  }
}

function handleDayInput(e: Event) {
  const input = e.target as HTMLInputElement
  let value = input.value.replace(/\D/g, '').slice(0, 2)

  // Auto-pad and advance
  if (value.length === 1 && parseInt(value) > 3) {
    value = '0' + value
  }

  day.value = value

  if (value.length === 2) {
    yearInput.value?.focus()
  }
}

function handleYearInput(e: Event) {
  const input = e.target as HTMLInputElement
  year.value = input.value.replace(/\D/g, '').slice(0, 4)
}

function handleBackspace(field: 'day' | 'year', e: KeyboardEvent) {
  if (e.key === 'Backspace') {
    if (field === 'day' && day.value === '') {
      monthInput.value?.focus()
    } else if (field === 'year' && year.value === '') {
      dayInput.value?.focus()
    }
  }
}

function handleSubmit() {
  showValidation.value = true

  const monthValid = validateMonth()
  const dayValid = validateDay()
  const yearValid = validateYear()

  if (!monthValid || !dayValid || !yearValid) {
    return
  }

  // Pad values
  const paddedMonth = month.value.padStart(2, '0')
  const paddedDay = day.value.padStart(2, '0')

  formStore.setField('dobMonth', paddedMonth)
  formStore.setField('dobDay', paddedDay)
  formStore.setField('dobYear', year.value)

  emit('submit')
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter') {
    handleSubmit()
  }
}
</script>

<template>
  <div class="w-full animate-slide-up">
    <div class="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mx-1">
      <!-- Date inputs -->
      <div class="flex gap-3">
        <!-- Month -->
        <div class="flex-1">
          <input
            ref="monthInput"
            :value="month"
            @input="handleMonthInput"
            type="text"
            inputmode="numeric"
            placeholder="MM"
            maxlength="2"
            @keydown="handleKeydown"
            :class="[
              'w-full py-3.5 px-4 border-2 rounded-xl text-base text-center transition-colors',
              showValidation && monthError
                ? 'border-red-400 bg-red-50'
                : 'border-gray-200 focus:border-primary'
            ]"
          />
        </div>

        <!-- Day -->
        <div class="flex-1">
          <input
            ref="dayInput"
            :value="day"
            @input="handleDayInput"
            @keydown="(e) => { handleBackspace('day', e); handleKeydown(e) }"
            type="text"
            inputmode="numeric"
            placeholder="DD"
            maxlength="2"
            :class="[
              'w-full py-3.5 px-4 border-2 rounded-xl text-base text-center transition-colors',
              showValidation && dayError
                ? 'border-red-400 bg-red-50'
                : 'border-gray-200 focus:border-primary'
            ]"
          />
        </div>

        <!-- Year -->
        <div class="flex-[1.5]">
          <input
            ref="yearInput"
            :value="year"
            @input="handleYearInput"
            @keydown="(e) => { handleBackspace('year', e); handleKeydown(e) }"
            type="text"
            inputmode="numeric"
            placeholder="YYYY"
            maxlength="4"
            :class="[
              'w-full py-3.5 px-4 border-2 rounded-xl text-base text-center transition-colors',
              showValidation && yearError
                ? 'border-red-400 bg-red-50'
                : 'border-gray-200 focus:border-primary'
            ]"
          />
        </div>
      </div>

      <!-- Error messages -->
      <div v-if="showValidation && (monthError || dayError || yearError)" class="mt-2">
        <p v-if="monthError" class="text-red-500 text-xs ml-1">{{ monthError }}</p>
        <p v-if="dayError" class="text-red-500 text-xs ml-1">{{ dayError }}</p>
        <p v-if="yearError" class="text-red-500 text-xs ml-1">{{ yearError }}</p>
      </div>

      <!-- Submit button -->
      <button
        @click="handleSubmit"
        class="w-full mt-5 py-4 bg-primary text-white rounded-full font-semibold text-base shadow-lg shadow-primary/30 hover:bg-primary-dark active:scale-[0.98] transition-all"
      >
        Continue
        <svg class="inline w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      <!-- FCRA Disclosure -->
      <p class="text-[10px] text-gray-500 mt-4 leading-relaxed text-center">
        By clicking "Continue", I provide express written consent, under the Fair Credit Reporting Act, allowing Array US, Inc. to obtain information from my personal credit report or other information from a credit bureau solely for debt settlement, which information may be shared with Array US, Inc. and First Advantage Financial LLC.
      </p>
    </div>
  </div>
</template>
